import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { ClientRequestModel } from '@aiwyn/api';
import {
  ClientRequestIdentityHelper,
  CommonClientRequestFactoryService,
} from '@aiwyn/common/client-requests';
import { DATA_SELECTOR_INJECTION_TOKEN, ignoreNullish, TrackService } from '@aiwyn/shared';
import { map, shareReplay, take } from 'rxjs/operators';
// OLD MODULE DEPENDENCY: actions and state from the legacy client-requests feature module.
// When the CXP module owns its own request detail view, replace these with CXP-native state
// and remove the ClientRequestsModule import below.
// Related files to clean up:
//   - src/app/features/client-requests/state/client-requests.actions.ts (SetSelectedClientRequestId, ClearSelectedClientRequestId, InitializeClientRequests)
//   - src/app/features/client-requests/state/client-requests.state.ts (selectedRequestId slice)
//   - src/app/features/client-requests/containers/client-request-details/ (ClientRequestDetailsComponent — does the same thing this container does)
import {
  ClearSelectedClientRequestId,
  ClientRequestsState,
} from '../../../client-requests/state';
import { ROUTE_TOKEN_CXP_TASKS, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { CxpTasksDataStateService } from '../../services/cxp-tasks-data-state.service';
import { CxpTasksSelectors } from '../../state/cxp-tasks-state.selectors';
// OLD MODULE DEPENDENCY: importing the entire ClientRequestsModule to get access to
// ClientRequestPanelComponent (awn-client-request-panel) and to register ClientRequestsState.
// When CXP has its own detail view, this import can be removed entirely.
import { ClientRequestsModule } from '../../../client-requests';

/**
 * Hosts the legacy ClientRequestPanelComponent within the CXP tasks flow.
 *
 * This is a transitional container: it reuses the old client-requests panel so that
 * task card clicks open request details inside the CXP routing context (no redirect to /projects).
 * When close is triggered, it navigates back to the tasks list instead of the projects page.
 *
 * All legacy state orchestration (InitializeClientRequests, SetSelectedClientRequestId,
 * GetClientRequestDetails) is handled by the background effect in CxpTasksDataStateService.
 * This container just reads the result from state.
 *
 * Future: replace with a CXP-native detail view that does not depend on ClientRequestsModule,
 * ClientRequestsState, or the old InitializeClientRequests/SetSelectedClientRequestId actions.
 */
@Component({
  selector: 'cxp-task-request-detail-container',
  template: `
    @if (instance(); as instance) {
      <awn-client-request-panel [instance]="instance"></awn-client-request-panel>
    }
  `,
  styleUrls: ['./task-request-detail-container.component.scss'],
  imports: [ClientRequestsModule],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'cxp-task-request-detail',
    },
  ],
})
export class TaskRequestDetailContainerComponent {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dataState = inject(CxpTasksDataStateService);
  private readonly trackService = inject(TrackService);
  // OLD MODULE DEPENDENCY: CommonClientRequestFactoryService lives in @aiwyn/common/client-requests.
  // It creates BaseCommonClientRequest instances that read from CommonClientRequestsState.
  private readonly factoryService = inject(CommonClientRequestFactoryService);

  // OLD MODULE DEPENDENCY: reads selectedRequestId from the legacy ClientRequestsState,
  // then creates an instance via the old factory service + identity helper.
  // Mirrors the pattern in: src/app/features/client-requests/containers/client-request-details/client-request-details.component.ts
  readonly instance = toSignal(
    this.store.select(ClientRequestsState.getSlices.selectedRequestId).pipe(
      ignoreNullish(),
      map((id) => ClientRequestIdentityHelper.getRequestIdentity({ id } as ClientRequestModel)),
      map((identity) => this.factoryService.getClientRequest(identity)),
      shareReplay(1),
    ),
  );

  constructor() {
    this.dataState.backgroundEffects$().pipe(takeUntilDestroyed()).subscribe();

    // OLD MODULE DEPENDENCY: the panel's close() dispatches ClearSelectedClientRequestId.
    // We intercept it here to navigate back to tasks instead of staying on a dead page.
    // See: src/app/features/client-requests/containers/client-request-details/client-request-panel/client-request-panel.component.ts — close()
    this.actions$.pipe(
      ofActionDispatched(ClearSelectedClientRequestId),
      take(1),
      takeUntilDestroyed(),
    ).subscribe(() => {
      const requestId = this.store.selectSnapshot(CxpTasksSelectors.getSelectedRequestId);
      this.trackService.track('CXP Task Detail Closed', { requestId: requestId ?? 0 });
      this.router.navigate([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS]);
    });
  }
}
